// useRunsDescribe - name a set of conversations by run id, so a link to one reads as its
// title. The runs sibling of useUploadsDescribe; mirrors
// lambda_handlers/api/stateful_agents/runs/describe.py.
import { useMemo } from "react";
import { useQuery } from "react-query";

import { useAgentsHttp } from "../useAgentsHttp";
import type { StatefulRun } from "./useStatefulAgents";
import { QK } from "../queryKeys";

export interface RunDescription {
  run_id: string;
  title: string;
  kind: StatefulRun["kind"];
  agent: string | null;
  created_at: string;
}


export interface DescribedRuns {
  byId: Map<string, RunDescription>;
  /** The lookup has answered, so an id missing from `byId` is one the viewer cannot
   *  open, not one still loading (as useUploadsDescribe). */
  resolved: boolean;
}

/** Names for `runIds`. A run the viewer cannot see (someone else's private one, or
 *  none at all) is simply absent. */
export const useRunsDescribe = (runIds: string[]): DescribedRuns => {
  const http = useAgentsHttp();
  // Sorted + deduped so one page mints one cache key, however often a run is cited.
  const ids = Array.from(new Set(runIds)).sort();
  const { data, isSuccess, isError } = useQuery<
    { runs: RunDescription[] },
    Error
  >(
    [QK.runsDescribe, ids.join(",")],
    () =>
      http<{ runs: RunDescription[] }>("/stateful-runs/describe", {
        run_ids: ids,
      }),
    { enabled: ids.length > 0, staleTime: 5 * 60 * 1000 },
  );
  // Memoised on the reply, so a renderer keyed on it (useCitations) is not rebuilt per render.
  const byId = useMemo(
    () => new Map((data?.runs ?? []).map((r) => [r.run_id, r])),
    [data],
  );
  return { byId, resolved: ids.length === 0 || isSuccess || isError };
};
